


























































import {
  Vue,
  Component,
  Prop,
  Watch,
} from "@/shared/vuepack";
import moment from "moment";
import { formatCurrency } from "@/shared/currency";
import _ from "lodash";
import ContributionsPieChart from "@/shared/components/contribution/ContributionsPieChart.vue";
import ContributionsDonutChart from "@/employee/components/contributionsV2/ContributionsDonutChart.vue";
import {
  FundingOption,
  CardWidgetTab,
  FeatureFlags
} from "@/shared/types";
import GenericLoadingSpinner from "@/shared/components/ui/GenericLoadingSpinner.vue";
import { Home, Contribution } from "@/employee/route-names";
import { PartnerAccountHolderDashboard } from "@/employer/route-names";
import { useCustodianSettingsStore } from "@/shared/store/custodianSettings";
import { useFeatureFlagStore } from "@/shared/store/featureFlag";
import { useHsaContributionSummary } from "@/shared/store/hsaContributionSummary";

@Component({
  components: {
    GenericLoadingSpinner,
    ContributionsDonutChart,
    ContributionsPieChart
  }
})
export default class ContributionsGraphWidget extends Vue {
  hsaContributionSummary = useHsaContributionSummary();
  featureFlagStore = useFeatureFlagStore();
  custodianSettingsStore = useCustodianSettingsStore();

  Contribution = Contribution;
  EmployeeHome = Home;

  @Prop()
  width: number;

  @Prop()
  height: number;

  @Prop({ required: false, default: false})
  forecastMode: boolean;

  @Prop({ required: false })
  allowedForecastYears: number[];

  @Prop({ required: false })
  currentForecastYear: number;

  @Prop({ required: false })
  forecastAmount: number;

  @Prop({ required: false, default: false })
  showForecastAmount: boolean;

  @Prop({ required: false })
  username: string;

  @Prop({ required: false })
  partnerId: number;

  @Prop( { required: false} )
  employeeAmount: number;

  @Prop({required: false, default: true})
  showYearSelection: boolean;

  @Prop( { required: false} )
  otherHsaAmount: number;

  @Prop()
  routeToContributions: Location;

  @Prop({ default: 0 })
  personalContributionAdjustment: number;

  @Prop({ default: "Contributions" })
  title: string;

  selectedYear = this.currentYearClock;
  tabIndex = 0;
  formatCurrency = formatCurrency;

  mounted (): void {
    this.loadData();
    this.handleYearChange(this.selectedYear);
  }

  loadData () {
    this.hsaContributionSummary.load(this.selectedYear, this.username, this.partnerId);
  }

  handleYearChange (value): void {
    this.selectedYear = value;

    if (this.forecastMode) {
      if (value !== this.currentForecastYear) {
        this.$emit("forecast-year-changed", value);
      }

      this.$nextTick(() => {
        // If switching to a forecasted year, show the projected tab. Show active otherwise
        this.tabIndex = this.allowedForecastYears.includes(value) ? 1 : 0;
      });
    }
  }

  get activeTabs (): CardWidgetTab[] {
    const tabs = [
      { title: "Actual", disabled: this.actualTabDisabled, visible: true },
      { title: "Projected", disabled: this.projectedTabDisabled, visible: this.projectedTabVisible }
    ];
    return tabs.filter(tab => tab.visible);
  }

  get projectedTabDisabled (): boolean {
    if (this.forecastMode) {
      return !this.showForecast;
    }

    return this.selectedYear < this.activePayrollYear;
  }

  get actualTabDisabled (): boolean {
    return this.selectedYear > this.activePayrollYear;
  }

  get projectedTabVisible (): boolean {
    return this.hasBendProjectedPayroll || this.hsaContributionSummary.showProjectedPersonalContributions;
  }

  get tooltipCopy (): string {
    if (!this.custodianSettingsStore.isHsaBankCustodian) {
      return "Only fully processed contributions are included in the Actual YTD Total";
    }

    if (this.tabIndex === 1) {
      return "Projected amounts include scheduled one-time and recurring contributions from your linked bank account(s). Employer and payroll contributions aren’t included.";
    }

    return "Actual amounts include fully processed contributions since Jan. 1 of this year.";
  }

  get hasBendProjectedPayroll (): boolean {
    return this.hsaContributionSummary.hasBendProjectedPayroll;
  }

  get showForecast (): boolean {
    // Forecast amount for current or future contributions is shown on the projected tab
    return this.forecastMode && this.allowedForecastYears.includes(this.selectedYear);
  }

  get projectedForecastAmount (): number {
    return this.showForecast ? this.forecastAmount : 0;
  }

  get selectedYearHsaLimit (): number {
    return this.hsaContributionSummary.hsaLimit(this.selectedYear);
  }

  get currentYearClock () {
    return moment().year();
  }

  yearsThrough (year: number): number[] {
    return _.range(this.hsaContributionSummary.firstPlanEffectiveYear, year + 1); // (from,to]
  }

  get contributionYearOptionsAllowingForFuturePayroll (): number[] {
    const nextYear = this.activePayrollYear ?
      this.currentYearClock + 1 : this.activePayrollYear + 1;

    return this.yearsThrough(nextYear);
  }

  get contributionYearOptionsActualOnly () {
    return this.yearsThrough(this.currentYearClock);
  }

  get yearOptions () {
    if (!this.showYearSelection) {
      return null;
    }
    if (this.custodianSettingsStore.isHsaBankCustodian) {
      return this.unfilteredYearOptions.filter(y => y >= 2022);
    }

    return this.unfilteredYearOptions;
  }

  get unfilteredYearOptions () {
    if (this.hasBendProjectedPayroll ||
        (this.forecastMode && this.allowedForecastYears.includes(this.activePayrollYear + 1))) {
      return this.contributionYearOptionsAllowingForFuturePayroll;
    }
    return this.contributionYearOptionsActualOnly;
  }

  get otherHsaContribution (): number {
    if (this.otherHsaAmount || this.otherHsaAmount === 0) {
      return this.otherHsaAmount;
    }
    return this.hsaContributionSummary.otherHsaAmount(this.selectedYear);
  }

  get employeePersonalContributions (): number {
    const result = this.annualEmployeePersonalContribution;

    // adjust for rounding in floating point precision
    return _.round(result, 2);
  }

  get bankDirectContribution (): number {
    const result = this.hsaContributionSummary.bankDirectContributions(this.selectedYear);

    // adjust for rounding in floating point precision

    return _.round(result, 2);
  }

  get employeePayrollContributions (): number {
    return this.hsaContributionSummary.employeePayrollContributions(this.selectedYear);
  }

  get projectedEmployeePayrollContributions (): number {
    // this projection can be overridden through the employeeAmount prop
    if (this.employeeAmount) {
      return this.employeeAmount;
    }

    const result = this.hsaContributionSummary.projectedEmployeePayrollContributions(this.selectedYear);
    return _.round(result, 2);
  }

  get projectedPersonalContributions (): number {
    return this.hsaContributionSummary.projectedPersonalContributions(this.selectedYear);
  }

  get projectedPersonalContributionAdjusted () {
    return Math.max(this.projectedPersonalContributions - this.personalContributionAdjustment, 0);
  }

  get annualEmployeePersonalContribution () {
    return this.hsaContributionSummary.annualEmployeePersonalContribution(this.selectedYear);
  }

  get annualEmployerContribution (): number {
    return this.hsaContributionSummary.annualEmployerContribution(this.selectedYear);
  }

  get projectedAnnualEmployerContribution () {
    return this.hsaContributionSummary.projectedAnnualEmployerContribution(this.selectedYear);
  }

  get activePayrollYear () {
    if (!this.hsaContributionSummary.activePayrollYear) {
      return 0;
    }
    return this.hsaContributionSummary.activePayrollYear;
  }

  @Watch("username")
  @Watch("partnerId")
  @Watch("selectedYear")
  triggerLoadData () {
    this.loadData();

    this.handleYearChange(this.selectedYear);
  }

  @Watch("currentForecastYear")
  forecastYearUpdated () {
    if (this.currentForecastYear !== this.selectedYear) {
      this.handleYearChange(this.currentForecastYear);
    }
  }

  get isLoaded () {
    return this.hsaContributionSummary.isLoaded(this.selectedYear);
  }

  get showRouterLink () {
    return this.featureFlagStore.flags.bend17151ContributionWidgetUpdate &&
      (this.$route.name === this.EmployeeHome || this.$route.name === PartnerAccountHolderDashboard);
  }

  tabUpdated (value: number) {
    this.tabIndex = value;
  }
}
